import { Injectable } from '@angular/core';

// ============================================================================
// Public types — consumed by create-post / edit-post components
// ============================================================================

export interface SeoScoreInput {
  title: string;
  keywords: string[];
  metaDescription: string | null;
  excerpt: string | null;
  coverImageUrl: string | null;
  coverImageAlt: string | null;
  tags: string[];
  /** Raw content string (HTML or Markdown). Used only for word-count check. */
  content: string;
}

export interface SeoCheckItem {
  label: string;
  /** Maximum points available for this check. */
  points: number;
  /** Points actually earned (0 … points). */
  earned: number;
  /** Convenience flag: true when earned === points. */
  passed: boolean;
  /** Actionable improvement hint shown when !passed. */
  hint?: string;
}

export interface SeoScoreResult {
  /** Aggregate score 0–100. */
  total: number;
  breakdown: SeoCheckItem[];
}

export type SeoTier = 'excellent' | 'good' | 'needs-work' | 'poor';

/**
 * Map a numeric SEO score to a named tier.
 * Use with colour classes: green / teal / amber / red.
 */
export function getSeoTier(score: number): SeoTier {
  if (score >= 80) return 'excellent';
  if (score >= 60) return 'good';
  if (score >= 40) return 'needs-work';
  return 'poor';
}

// ============================================================================
// Service
// ============================================================================

/**
 * Stateless service that computes an SEO health score from post form values.
 *
 * **Score is purely a client-side authoring aid — it is NEVER persisted.**
 *
 * Scoring breakdown (100 points total):
 *
 * | Check                              | Pts |
 * |------------------------------------|-----|
 * | Title length (40–65 chars)         |  10 |
 * | Focus keyword in title             |  15 |
 * | Keywords field populated (≥ 1)     |  10 |
 * | Meta description filled            |  10 |
 * | Meta description length (120–160)  |  10 |
 * | Focus keyword in meta description  |  10 |
 * | Excerpt filled                     |   8 |
 * | Cover image present                |   8 |
 * | Cover image alt text               |   7 |
 * | Tags (≥ 3)                         |   7 |
 * | Content word count ≥ 300           |   5 |
 */
@Injectable({ providedIn: 'root' })
export class SeoScoreService {

  compute(input: SeoScoreInput): SeoScoreResult {
    const checks: SeoCheckItem[] = [
      this._checkTitleLength(input.title),
      this._checkKeywordInTitle(input.title, input.keywords),
      this._checkKeywordsPopulated(input.keywords),
      this._checkMetaDescriptionFilled(input.metaDescription),
      this._checkMetaDescriptionLength(input.metaDescription),
      this._checkKeywordInMetaDescription(input.metaDescription, input.keywords),
      this._checkExcerpt(input.excerpt),
      this._checkCoverImage(input.coverImageUrl),
      this._checkCoverImageAlt(input.coverImageAlt),
      this._checkTags(input.tags),
      this._checkWordCount(input.content),
    ];
    const total = checks.reduce((sum, c) => sum + c.earned, 0);
    return { total, breakdown: checks };
  }

  // --------------------------------------------------------------------------
  // Individual checks (pure functions wrapped in methods)
  // --------------------------------------------------------------------------

  private _checkTitleLength(title: string): SeoCheckItem {
    const len = (title ?? '').trim().length;
    let earned = 0;
    let hint: string | undefined;

    if (len >= 40 && len <= 65) {
      earned = 10;
    } else if ((len >= 20 && len < 40) || (len > 65 && len <= 80)) {
      earned = 5;
      hint = len < 40
        ? 'Aim for 40–65 characters for best click-through rate.'
        : 'Title may be truncated in search results — keep it under 65 chars.';
    } else {
      hint = len < 20
        ? 'Title is too short. Aim for at least 40 characters.'
        : 'Title is too long (> 80 chars). Search engines will truncate it.';
    }
    return { label: 'Title length (40–65 chars)', points: 10, earned, passed: earned === 10, hint };
  }

  private _checkKeywordInTitle(title: string, keywords: string[]): SeoCheckItem {
    if (!keywords.length) {
      return {
        label: 'Focus keyword in title',
        points: 15, earned: 0, passed: false,
        hint: 'Add a focus keyword first.',
      };
    }
    const focus = keywords[0].toLowerCase();
    const found = (title ?? '').toLowerCase().includes(focus);
    return {
      label: 'Focus keyword in title',
      points: 15,
      earned: found ? 15 : 0,
      passed: found,
      hint: found ? undefined : `Add "${keywords[0]}" to your title.`,
    };
  }

  private _checkKeywordsPopulated(keywords: string[]): SeoCheckItem {
    const passed = (keywords ?? []).length >= 1;
    return {
      label: 'Keywords populated (≥ 1)',
      points: 10,
      earned: passed ? 10 : 0,
      passed,
      hint: passed ? undefined : 'Add at least one focus keyword.',
    };
  }

  private _checkMetaDescriptionFilled(metaDescription: string | null): SeoCheckItem {
    const passed = !!metaDescription && metaDescription.trim().length > 0;
    return {
      label: 'Meta description filled',
      points: 10,
      earned: passed ? 10 : 0,
      passed,
      hint: passed ? undefined : 'Write a meta description to control your search snippet.',
    };
  }

  private _checkMetaDescriptionLength(metaDescription: string | null): SeoCheckItem {
    const len = (metaDescription ?? '').trim().length;
    let earned = 0;
    let hint: string | undefined;

    if (len >= 120 && len <= 160) {
      earned = 10;
    } else if (len >= 80 && len < 120) {
      earned = 5;
      hint = 'Meta description is a bit short — aim for 120–160 characters.';
    } else if (len > 0) {
      hint = len < 80
        ? 'Meta description is too short. Target 120–160 characters.'
        : 'Meta description is too long and will be truncated — keep under 160 chars.';
    } else {
      hint = 'No meta description entered.';
    }
    return { label: 'Meta description length (120–160)', points: 10, earned, passed: earned === 10, hint };
  }

  private _checkKeywordInMetaDescription(
    metaDescription: string | null,
    keywords: string[],
  ): SeoCheckItem {
    if (!keywords.length) {
      return {
        label: 'Focus keyword in meta description',
        points: 10, earned: 0, passed: false,
        hint: 'Add a focus keyword first.',
      };
    }
    const focus = keywords[0].toLowerCase();
    const found = (metaDescription ?? '').toLowerCase().includes(focus);
    return {
      label: 'Focus keyword in meta description',
      points: 10,
      earned: found ? 10 : 0,
      passed: found,
      hint: found ? undefined : `Include "${keywords[0]}" in your meta description.`,
    };
  }

  private _checkExcerpt(excerpt: string | null): SeoCheckItem {
    const passed = !!excerpt && excerpt.trim().length > 0;
    return {
      label: 'Excerpt filled',
      points: 8,
      earned: passed ? 8 : 0,
      passed,
      hint: passed ? undefined : 'Add an excerpt — used as fallback for search snippets.',
    };
  }

  private _checkCoverImage(coverImageUrl: string | null): SeoCheckItem {
    const passed = !!coverImageUrl && coverImageUrl.trim().length > 0;
    return {
      label: 'Cover image present',
      points: 8,
      earned: passed ? 8 : 0,
      passed,
      hint: passed ? undefined : 'Add a cover image for Open Graph / Twitter sharing.',
    };
  }

  private _checkCoverImageAlt(coverImageAlt: string | null): SeoCheckItem {
    const passed = !!coverImageAlt && coverImageAlt.trim().length > 0;
    return {
      label: 'Cover image alt text',
      points: 7,
      earned: passed ? 7 : 0,
      passed,
      hint: passed ? undefined : 'Add alt text for accessibility and Google Image Search.',
    };
  }

  private _checkTags(tags: string[]): SeoCheckItem {
    const count = (tags ?? []).length;
    const earned = count >= 3 ? 7 : count >= 1 ? 4 : 0;
    return {
      label: 'Tags (≥ 3 recommended)',
      points: 7,
      earned,
      passed: count >= 3,
      hint: count < 3
        ? `Add ${3 - count} more tag(s) for better categorisation.`
        : undefined,
    };
  }

  private _checkWordCount(content: string): SeoCheckItem {
    // Strip HTML tags and common Markdown syntax before counting
    const stripped = (content ?? '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/[#*`_~[\]()]/g, ' ');
    const wordCount = stripped.trim().split(/\s+/).filter(w => w.length > 0).length;
    const passed = wordCount >= 300;
    return {
      label: 'Content word count (≥ 300)',
      points: 5,
      earned: passed ? 5 : 0,
      passed,
      hint: passed
        ? undefined
        : `${wordCount} word(s) — aim for at least 300 to avoid thin-content issues.`,
    };
  }
}
